// Pairing the phone with its device row, and reading its details back afterwards.

export interface ScanArea {
  id: number
  name: string
}

interface SessionResponse {
  purpose: ScanDevicePurpose
  partner_name: string | null
  device_label: string
  event: { id: number, name: string, timezone?: string } | null
  areas: ScanArea[]
  // Sent by the server rather than built into the app, so the window can be
  // shortened without waiting for a release.
  retention_days?: number
}

interface ProvisionResponse extends SessionResponse {
  token: string
}

// Both calls here are bounded, for the same reason the door's are: a fetch with no
// ceiling does not fail, it waits, and every recovery path below is written for the case
// where we cannot be reached. Without one they are unreachable code.
//
// The two numbers are far apart because they are picked by what giving up costs, not by
// how long somebody will stand there. ofetch's timeout aborts this end, not the server's.
//
// Pairing is the one place in this app where giving up early is worse than waiting. The
// setup code is single use and the server clears it the moment it accepts one, so a
// timeout firing after we committed leaves the phone unpaired with its code spent, and
// somebody has to issue a new one from the admin screen. Hence ten seconds rather than
// the door's three. ofetch does not retry a POST by default and that is left alone here:
// a second attempt would only double the chance of spending a code for nothing.
const PROVISION_TIMEOUT_MS = 10000

// Opening, where the opposite applies. Giving up costs nothing, because the fallback is
// the area list the phone already has, and the alternative is an app that does not start
// at all on a stalled connection.
const SESSION_TIMEOUT_MS = 4000

export const useProvisioning = () => {
  const config = useRuntimeConfig()
  const device = useScanDevice()
  // Starts from what the phone last knew, so a screen reading this has the remembered
  // list rather than nothing while loadSession is still going, or after it failed.
  const areas = useState<ScanArea[]>('scan-areas', () => device.value.knownAreas)
  // Read here rather than after an await. Nuxt loses track of which app it is inside
  // once an async call has happened, and a composable called then throws.
  const scanQueue = useScanQueue()
  // Same rule, and this call is also what starts the native lookups: reportIdentity
  // below only waits for them.
  const nativeInfo = useNativeInfo()

  /**
   * Exchanges the setup code for the phone's key. Returns an error message to show,
   * or null when it worked.
   */
  const provision = async (code: string): Promise<string | null> => {
    try {
      const response = await $fetch<ProvisionResponse>(
        `${config.public.apiBase}/scan/v1/provision`,
        {
          method: 'POST',
          timeout: PROVISION_TIMEOUT_MS,
          body: { code: code.trim().toUpperCase() },
        },
      )

      device.value = {
        provisioned: true,
        token: response.token,
        // A new pairing every time a code is exchanged. This is what stops a phone
        // handed to another partner uploading or counting what the last one scanned.
        pairingId: crypto.randomUUID(),
        // Decides which screen this phone lives on for the rest of its life.
        purpose: response.purpose ?? 'lead_capture',
        partnerName: response.partner_name ?? '',
        deviceLabel: response.device_label,
        eventId: response.event?.id ?? null,
        eventTimeZone: response.event?.timezone ?? '',
        // Left empty on purpose. Every app open asks which area again.
        areaId: null,
        areaName: '',
        // Same for the door: a room runs a different workshop after lunch.
        sessionId: null,
        sessionTitle: '',
        sessionRoom: '',
        sessionDate: '',
        sessionTime: '',
        // Remembered from this first answer, so a phone paired at the desk and carried
        // to an area with no signal can still be told where it is standing.
        knownAreas: response.areas,
        knownSessions: [],
      }

      areas.value = response.areas

      // Pairing is the first moment this phone has a key, and the plugin that reports
      // identity on opening has already run and found none to report against. Without
      // this a newly set up phone stays unidentified on the dashboard until somebody
      // reopens the app or presses the help button — which is precisely the emergency
      // this was meant to have answered in advance.
      //
      // Not awaited. The screen moves on to the area picker as soon as pairing worked,
      // and the request survives that.
      reportIdentity()

      return null
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status

      if (status === 403) {
        return 'That code was not recognized. Check the setup sheet.'
      }

      if (status === 429) {
        return 'Too many attempts. Wait a minute and try again.'
      }

      return 'Could not reach WeAreDevelopers. Check the connection and try again.'
    }
  }

  /**
   * Reads the phone's details back on opening. The areas are fetched fresh rather than
   * taken from the last run, which picks up an area the partner added since.
   *
   * The remembered list is a fallback, not the source: it is used only when the server
   * cannot be reached, because the alternative is a phone that cannot be told where it is
   * standing and therefore cannot scan at all. A stale area name on a phone that is
   * working is a far smaller problem than a phone that will not work.
   *
   * A key the server no longer accepts means the phone has been deactivated, and
   * the pairing is cleared so the app says so plainly.
   */
  const loadSession = async (): Promise<'ok' | 'deactivated' | 'unreachable'> => {
    if (!device.value.token) {
      return 'deactivated'
    }

    try {
      const response = await $fetch<SessionResponse>(
        `${config.public.apiBase}/scan/v1/session`,
        {
          headers: { Authorization: `Bearer ${device.value.token}` },
          timeout: SESSION_TIMEOUT_MS,
          // Overrides ofetch's default of one retry on a GET. That retry cannot be
          // bounded - it reuses the first attempt's signal, so it either fails instantly
          // or runs with no ceiling at all, depending on how the first one failed. The
          // reasoning is written out at ADMIT_ATTEMPTS in useWorkshopDoor.
          retry: 0,
        },
      )

      // Re-read on every open so a phone repurposed between shifts lands on the right
      // screen without being paired again.
      device.value.purpose = response.purpose ?? 'lead_capture'
      device.value.partnerName = response.partner_name ?? ''
      device.value.deviceLabel = response.device_label
      device.value.eventId = response.event?.id ?? null
      device.value.eventTimeZone = response.event?.timezone ?? ''
      areas.value = response.areas
      device.value.knownAreas = response.areas

      // The retention ceiling, applied on opening rather than on a timer, since iOS
      // gives a web app no time to run in the background.
      //
      // Deliberately not waited on. Housekeeping must never hold up the area list, and
      // a phone whose storage misbehaves has to stay usable.
      if (response.retention_days) {
        scanQueue.purge(response.retention_days).catch(() => {})
      }

      return 'ok'
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status

      if (status === 403) {
        // The pairing id outlives the key deliberately. Anything left waiting then still
        // names the pairing it was taken under rather than nothing at all.
        device.value = { ...emptyScanDevice(), pairingId: device.value.pairingId }

        return 'deactivated'
      }

      // Anything else is treated as a connection problem. A phone that cannot reach
      // us must still be able to scan, which is the whole point of the local store — so
      // it falls back to the areas it was last told about rather than showing none.
      areas.value = device.value.knownAreas

      return 'unreachable'
    }
  }

  /**
   * Tells the server which handset this is, so the identity is known before anything
   * goes wrong rather than only in the message sent once it has.
   *
   * Deliberately its own request rather than something folded into loadSession. Opening
   * the app is the one path that has to work on a stalled connection at an unattended
   * area, and it is already written to fall back to the remembered area list and carry
   * on. Hanging a second concern off it would put a phone's ability to start scanning
   * behind a piece of book-keeping, which is the wrong way round. Separate also means an
   * older APK that never calls this is simply a phone we learn about from its next
   * support request.
   *
   * Never awaited by the caller and never throws.
   */
  const reportIdentity = async (): Promise<void> => {
    if (!device.value.token) {
      return
    }

    await nativeInfoReady()

    const identity = identityPayload(nativeInfo.value)

    // The iPhones, and any phone whose managed configuration has not landed. Nothing to
    // say means no request at all rather than an empty one: the whole home screen fleet
    // would otherwise make a pointless call on every open.
    if (!Object.keys(identity).length) {
      return
    }

    try {
      await $fetch(`${config.public.apiBase}/scan/v1/identity`, {
        method: 'POST',
        // Bounded like everything else here, and short. Nobody is waiting on this and
        // nothing downstream reads the answer, so a stalled request should let go
        // rather than sit on a connection the uploads need.
        timeout: SESSION_TIMEOUT_MS,
        headers: { Authorization: `Bearer ${device.value.token}` },
        body: identity,
      })
    } catch {
      // Housekeeping. A phone that could not report itself still scans, and the serial
      // travels with the next support request regardless of whether this ever lands.
    }
  }

  return { provision, loadSession, reportIdentity, areas }
}
